import { createContext, useContext, useState, useEffect, useMemo } from 'react';

export const AppContext = createContext(null);

export const useAppContext = () => {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useAppContext must be used within AppContext.Provider');
  return ctx;
};

// The wall-clock values tick every 30-60s, 24/7, on always-open POS screens.
// Keeping them in their own provider means those ticks re-render only the
// components that actually display elapsed time (kitchen/queue timers) instead
// of the entire AppContext consumer tree.
export const ClockContext = createContext(null);

export const useClock = () => {
  const ctx = useContext(ClockContext);
  if (!ctx) throw new Error('useClock must be used within ClockProvider');
  return ctx;
};

export const ClockProvider = ({ fastTick = false, children }) => {
  const [now, setNow] = useState(() => Date.now());
  const [kitchenNow, setKitchenNow] = useState(() => Date.now());

  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 60000);
    return () => clearInterval(interval);
  }, []);

  // Faster tick only while the kitchen display is active (mirrors the original
  // kitchenDisplayMode gate).
  useEffect(() => {
    if (!fastTick) return undefined;
    const interval = setInterval(() => setKitchenNow(Date.now()), 30000);
    return () => clearInterval(interval);
  }, [fastTick]);

  const value = useMemo(() => ({ now, kitchenNow }), [now, kitchenNow]);

  return <ClockContext.Provider value={value}>{children}</ClockContext.Provider>;
};
